import type { PrinterState } from "@luckymenu/core";
import type { Printer, PrintJob } from "@luckymenu/print-core";
import type { PrinterReach } from "./service";

/**
 * What the printers list and the job list show (P5-T24, P5-T26), the same
 * in both apps.
 */

// LAN addresses follow one rule, which the API checks too (P12-T06).
export { formatLanAddress, type LanAddress, parseLanAddress } from "@luckymenu/print-core";

/** A station's report counts for this long; a device that stopped reporting no longer does. */
export const STATION_FRESH_MS = 3 * 60_000;

export type PrinterHealth = {
	/** `ready`: someone reaches it and nothing is wrong. `problem`: paper, cover or offline. `unreached`: no device reaches it. */
	kind: "ready" | "problem" | "unreached";
	/** The last problem seen, for its words. */
	state: PrinterState | null;
	/** Devices that reached it in the last 3 minutes, this one included. */
	reachedBy: number;
	/** This device: reaches it, does not, or has no way to try (null). */
	here: boolean | null;
	/** Some device prints its jobs by itself: another one, or this one with its switch on. */
	hasStation: boolean;
};

/**
 * A printer's status from every device's reports and this device's own
 * check. `autoPrintHere`: this device prints the printer's jobs by itself
 * (`PrintService.printsHereByItself`); reaching it is not enough.
 */
export function printerHealth(
	printer: Printer,
	local: PrinterReach | undefined,
	deviceId: string | null,
	now: number,
	autoPrintHere: boolean,
): PrinterHealth {
	const fresh = printer.stations.filter(
		(s) => s.deviceId !== deviceId && now - new Date(s.lastCheckedAt).getTime() < STATION_FRESH_MS,
	);
	const others = fresh.filter((s) => s.isReachable);
	const here = local ? local.reachable : null;
	const reachedBy = others.length + (here ? 1 : 0);
	const states = [...(local?.reachable ? [local.state] : []), ...others.map((s) => s.state)].filter(
		(s) => s === "paper_out" || s === "cover_open" || s === "offline",
	);
	const state = states[0] ?? null;
	return {
		kind: reachedBy === 0 ? "unreached" : state ? "problem" : "ready",
		state,
		reachedBy,
		here,
		hasStation: others.some((s) => s.autoPrint) || (here === true && autoPrintHere),
	};
}

/** The i18n key under `printing:` for a printer state. */
export const PRINTER_STATE_KEYS: Record<PrinterState, string> = {
	ready: "state.ready",
	paper_out: "state.paperOut",
	cover_open: "state.coverOpen",
	offline: "state.offline",
	unknown: "state.unknown",
};

/** Jobs a person must look at: failed, or waiting with nobody printing them for 20 seconds. */
export function needsAttention(job: PrintJob, now: number, afterMs: number): boolean {
	if (job.status === "failed") return true;
	return job.status === "queued" && now - new Date(job.queuedAt).getTime() >= afterMs;
}
